import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDateTimeIST } from "@/lib/format";
import { DatePicker } from "@/components/pickers";
import { ScrollText } from "lucide-react";

export default function AuditLog() {
  const [filters, setFilters] = useState({ org_id: "", date_from: "", date_to: "" });
  const [applied, setApplied] = useState({ org_id: "", date_from: "", date_to: "" });

  const { data: orgs = [] } = useQuery({ queryKey: ["orgs-min"], queryFn: async () => (await api.get("/admin/organisations")).data });

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["audit", applied],
    queryFn: async () => {
      const params = {};
      if (applied.org_id) params.org_id = applied.org_id;
      if (applied.date_from) params.date_from = applied.date_from;
      if (applied.date_to) params.date_to = applied.date_to;
      return (await api.get("/admin/audit-log", { params })).data;
    },
  });

  return (
    <div>
      <PageHeader title="Audit Log" description="Every administrative action, with the real actor recorded" />

      <Card className="mb-5 flex flex-wrap items-end gap-4 p-4">
        <div className="min-w-[180px]">
          <Label className="text-xs">Organisation</Label>
          <Select value={filters.org_id || "all"} onValueChange={(v) => setFilters({ ...filters, org_id: v === "all" ? "" : v })}>
            <SelectTrigger className="mt-1.5 h-9" data-testid="audit-org-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All organisations</SelectItem>
              {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40"><Label className="text-xs">From</Label><div className="mt-1.5"><DatePicker mode="iso" value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} clearable placeholder="Any" testid="audit-from" /></div></div>
        <div className="w-40"><Label className="text-xs">To</Label><div className="mt-1.5"><DatePicker mode="iso" value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} clearable placeholder="Any" testid="audit-to" /></div></div>
        <Button variant="outline" onClick={() => setApplied({ ...filters })} data-testid="audit-apply">Apply</Button>
      </Card>

      {isLoading ? <p className="text-sm text-slate-500">Loading…</p> : logs.length === 0 ? (
        <EmptyState title="No audit entries" hint="Actions will appear here" icon={ScrollText} />
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead><TableHead>Actor</TableHead><TableHead>Organisation</TableHead>
                <TableHead>Action</TableHead><TableHead>Entity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((lg) => (
                <TableRow key={lg.id} data-testid={`audit-row-${lg.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">{formatDateTimeIST(lg.at)}</TableCell>
                  <TableCell className="text-sm" data-testid="audit-actor">{lg.actor_email || "-"}</TableCell>
                  <TableCell className="text-sm">{lg.org_name || "-"}</TableCell>
                  <TableCell className="text-sm font-medium capitalize">{String(lg.action).replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-xs text-slate-500">{lg.entity}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
